import React, { useState, useEffect } from "react";
import axios from "axios";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Save, GripVertical } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, rectSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SortableImage = ({ id, image, index, removeImage }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 1,
    opacity: isDragging ? 0.7 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group" data-testid={`image-${index}`}>
      <div
        {...attributes}
        {...listeners}
        className="absolute top-2 left-2 z-10 bg-black/50 text-white w-7 h-7 rounded flex items-center justify-center cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
        title="Dragðu til að endurraða"
      >
        <GripVertical className="w-4 h-4" />
      </div>
      <img
        src={image}
        alt={`Property ${index + 1}`}
        className={`w-full h-32 object-cover rounded-lg border-2 ${isDragging ? "border-primary shadow-lg" : "border-transparent"}`}
        onError={(e) => {
          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23ddd" width="100" height="100"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%23999">Error</text></svg>';
        }}
      />
      <button
        type="button"
        onClick={() => removeImage(index)}
        className="absolute top-2 right-2 bg-red-600 text-white w-6 h-6 rounded-full hover:bg-red-700 opacity-0 group-hover:opacity-100 transition-opacity"
        data-testid={`remove-image-${index}`}
      >
        x
      </button>
      {index === 0 && (
        <div className="absolute bottom-2 left-2 bg-primary text-white px-2 py-1 rounded text-xs font-ui">
          Hero
        </div>
      )}
    </div>
  );
};

const PropertyManagement = () => {
  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [property, setProperty] = useState({
    name: "",
    description: "",
    description_is: "",
    description_lt: "",
    location: "",
    bedrooms: 0,
    bathrooms: 0,
    max_guests: 0,
    size_sqm: 0,
    amenities: [],
    images: [],
  });
  const [amenityInput, setAmenityInput] = useState("");
  const [imageInput, setImageInput] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = property.images.indexOf(active.id);
    const newIndex = property.images.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    setProperty({ ...property, images: arrayMove(property.images, oldIndex, newIndex) });
    toast.info("Röð breytt - mundu að vista!");
  };

  useEffect(() => {
    fetchProperty();
  }, []);

  const fetchProperty = async () => {
    try {
      const response = await axios.get(`${API}/property`);
      setProperty(response.data);
    } catch (error) {
      console.error("Error fetching property:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.put(`${API}/property`, property, {
        timeout: 120000, // 2 minute timeout for many images
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });
      
      toast.success("Eign uppfærð!");
      fetchProperty();
    } catch (error) {
      console.error("Update error:", error);
      if (error.code === 'ECONNABORTED') {
        toast.error("Timeout - reyndu aftur eða minnkaðu myndir.");
      } else {
        toast.error(error.response?.data?.detail || "Villa við að uppfæra eign. Reyndu aftur.");
      }
    } finally {
      setLoading(false);
    }
  };

  const addAmenity = () => {
    if (amenityInput.trim()) {
      setProperty({
        ...property,
        amenities: [...property.amenities, amenityInput.trim()],
      });
      setAmenityInput("");
    }
  };

  const removeAmenity = (index) => {
    setProperty({
      ...property,
      amenities: property.amenities.filter((_, i) => i !== index),
    });
  };

  const addImage = () => {
    if (imageInput.trim()) {
      setProperty({
        ...property,
        images: [...property.images, imageInput.trim()],
      });
      setImageInput("");
    }
  };

  const removeImage = (index) => {
    setProperty({
      ...property,
      images: property.images.filter((_, i) => i !== index),
    });
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files || files.length === 0) return;

    setUploadingImages(true);
    toast.info(`Hleð upp ${files.length} ${files.length === 1 ? 'mynd' : 'myndum'}...`);

    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      files.forEach(f => formData.append("files", f));

      const response = await axios.post(`${API}/images/upload-multiple`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        timeout: 120000,
      });

      const urls = response.data.urls.map(url => `${BACKEND_URL}${url}`);
      if (urls.length > 0) {
        setProperty({
          ...property,
          images: [...property.images, ...urls],
        });
        toast.success(`${urls.length} ${urls.length === 1 ? 'mynd' : 'myndir'} hlaðið upp!`);
      }
    } catch (error) {
      toast.error("Villa við að hlaða upp myndum");
    } finally {
      setUploadingImages(false);
      e.target.value = '';
    }
  };

  return (
    <AdminLayout>
      <div>
        <h1 className="text-4xl md:text-5xl font-display font-bold tracking-tight mb-8" data-testid="property-title">
          Property Management
        </h1>

        <form onSubmit={handleSubmit} className="bg-white border border-neutral-200 rounded-lg p-6 md:p-8" data-testid="property-form">
          <div className="space-y-6">
            {/* Basic Info */}
            <div>
              <h2 className="text-2xl font-heading font-semibold mb-4">Basic Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="col-span-full">
                  <Label htmlFor="name" className="text-sm font-ui font-medium text-neutral-700 mb-2 block">
                    Property Name
                  </Label>
                  <Input
                    id="name"
                    value={property.name}
                    onChange={(e) => setProperty({ ...property, name: e.target.value })}
                    required
                    className="px-4 py-3 rounded-lg"
                    data-testid="form-input-name"
                  />
                </div>

                <div className="col-span-full">
                  <Label htmlFor="description" className="text-sm font-ui font-medium text-neutral-700 mb-2 block">
                    Description (English) 🇬🇧
                  </Label>
                  <Textarea
                    id="description"
                    value={property.description}
                    onChange={(e) => setProperty({ ...property, description: e.target.value })}
                    required
                    className="px-4 py-3 rounded-lg min-h-[120px]"
                    placeholder="English description..."
                    data-testid="form-textarea-description"
                  />
                </div>

                <div className="col-span-full">
                  <Label htmlFor="description_is" className="text-sm font-ui font-medium text-neutral-700 mb-2 block">
                    Lýsing (Íslenska) 🇮🇸
                  </Label>
                  <Textarea
                    id="description_is"
                    value={property.description_is || ""}
                    onChange={(e) => setProperty({ ...property, description_is: e.target.value })}
                    className="px-4 py-3 rounded-lg min-h-[120px]"
                    placeholder="Íslensk lýsing..."
                    data-testid="form-textarea-description-is"
                  />
                </div>

                <div className="col-span-full">
                  <Label htmlFor="description_lt" className="text-sm font-ui font-medium text-neutral-700 mb-2 block">
                    Aprašymas (Lietuvių) 🇱🇹
                  </Label>
                  <Textarea
                    id="description_lt"
                    value={property.description_lt || ""}
                    onChange={(e) => setProperty({ ...property, description_lt: e.target.value })}
                    className="px-4 py-3 rounded-lg min-h-[120px]"
                    placeholder="Lietuviškas aprašymas..."
                    data-testid="form-textarea-description-lt"
                  />
                </div>

                <div className="col-span-full">
                  <Label htmlFor="location" className="text-sm font-ui font-medium text-neutral-700 mb-2 block">
                    Location
                  </Label>
                  <Input
                    id="location"
                    value={property.location}
                    onChange={(e) => setProperty({ ...property, location: e.target.value })}
                    required
                    className="px-4 py-3 rounded-lg"
                    data-testid="form-input-location"
                  />
                </div>

                <div className="col-span-full">
                  <Label htmlFor="contact_phone" className="text-sm font-ui font-medium text-neutral-700 mb-2 block">
                    Símanúmer (sýnilegt á "Hafa samband")
                  </Label>
                  <Input
                    id="contact_phone"
                    value={property.contact_phone || ""}
                    onChange={(e) => setProperty({ ...property, contact_phone: e.target.value })}
                    placeholder="+354 xxx xxxx"
                    className="px-4 py-3 rounded-lg"
                    data-testid="form-input-phone"
                  />
                </div>
              </div>
            </div>

            {/* Property Details */}
            <div>
              <h2 className="text-2xl font-heading font-semibold mb-4">Property Details</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                  <Label htmlFor="bedrooms" className="text-sm font-ui font-medium text-neutral-700 mb-2 block">
                    Bedrooms
                  </Label>
                  <Input
                    id="bedrooms"
                    type="number"
                    min="0"
                    value={property.bedrooms}
                    onChange={(e) => setProperty({ ...property, bedrooms: parseInt(e.target.value) })}
                    required
                    className="px-4 py-3 rounded-lg"
                    data-testid="form-input-bedrooms"
                  />
                </div>

                <div>
                  <Label htmlFor="bathrooms" className="text-sm font-ui font-medium text-neutral-700 mb-2 block">
                    Bathrooms
                  </Label>
                  <Input
                    id="bathrooms"
                    type="number"
                    min="0"
                    value={property.bathrooms}
                    onChange={(e) => setProperty({ ...property, bathrooms: parseInt(e.target.value) })}
                    required
                    className="px-4 py-3 rounded-lg"
                    data-testid="form-input-bathrooms"
                  />
                </div>

                <div>
                  <Label htmlFor="max_guests" className="text-sm font-ui font-medium text-neutral-700 mb-2 block">
                    Max Guests
                  </Label>
                  <Input
                    id="max_guests"
                    type="number"
                    min="1"
                    value={property.max_guests}
                    onChange={(e) => setProperty({ ...property, max_guests: parseInt(e.target.value) })}
                    required
                    className="px-4 py-3 rounded-lg"
                    data-testid="form-input-maxguests"
                  />
                </div>

                <div>
                  <Label htmlFor="size" className="text-sm font-ui font-medium text-neutral-700 mb-2 block">
                    Size (sqm)
                  </Label>
                  <Input
                    id="size"
                    type="number"
                    min="0"
                    value={property.size_sqm}
                    onChange={(e) => setProperty({ ...property, size_sqm: parseInt(e.target.value) })}
                    required
                    className="px-4 py-3 rounded-lg"
                    data-testid="form-input-size"
                  />
                </div>
              </div>
            </div>

            {/* Amenities */}
            <div>
              <h2 className="text-2xl font-heading font-semibold mb-4">Amenities</h2>
              <div className="flex gap-2 mb-4">
                <Input
                  value={amenityInput}
                  onChange={(e) => setAmenityInput(e.target.value)}
                  placeholder="Add an amenity"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAmenity())}
                  className="px-4 py-3 rounded-lg"
                  data-testid="form-input-amenity"
                />
                <Button type="button" onClick={addAmenity} className="bg-secondary hover:bg-secondary-700 hover:text-white" data-testid="add-amenity-button">
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {property.amenities.map((amenity, index) => (
                  <div
                    key={index}
                    className="bg-primary-50 text-primary-700 px-4 py-2 rounded-full font-ui text-sm flex items-center gap-2"
                    data-testid={`amenity-tag-${index}`}
                  >
                    {amenity}
                    <button
                      type="button"
                      onClick={() => removeAmenity(index)}
                      className="hover:text-primary-900"
                      data-testid={`remove-amenity-${index}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Images */}
            <div>
              <h2 className="text-2xl font-heading font-semibold mb-4">Myndir / Images</h2>
              
              {/* File Upload */}
              <div className="mb-4">
                <Label className="text-sm font-ui font-medium text-neutral-700 mb-2 block">
                  Hlaða upp myndum / Upload Images
                </Label>
                <Input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  disabled={uploadingImages}
                  className="px-4 py-3 rounded-lg cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="form-input-image-upload"
                />
                {uploadingImages && (
                  <div className="mt-2 p-3 bg-primary-50 border border-primary-200 rounded-lg">
                    <p className="text-sm text-primary-700 font-medium flex items-center gap-2">
                      <span className="animate-spin">⏳</span>
                      Vinnur úr myndum...
                    </p>
                  </div>
                )}
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-800 font-medium mb-1">
                    📱 Í síma: Þú getur valið margar myndir í einu
                  </p>
                  <p className="text-xs text-blue-600">
                    • Veldu "Photo Library" eða "Myndasafn"<br/>
                    • Ýttu á "Select" eða "Velja"<br/>
                    • Hakaðu við allar myndirnar sem þú vilt<br/>
                    • Smelltu á "Done" eða "Lokið"
                  </p>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  💻 Í tölvu: Haltu Ctrl/Cmd og smelltu á margar myndir
                </p>
              </div>

              {/* URL Input (optional) */}
              <div className="mb-4">
                <Label className="text-sm font-ui font-medium text-neutral-700 mb-2 block">
                  Eða settu inn mynd URL (valfrjálst)
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={imageInput}
                    onChange={(e) => setImageInput(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addImage())}
                    className="px-4 py-3 rounded-lg"
                    data-testid="form-input-image-url"
                  />
                  <Button type="button" onClick={addImage} className="bg-secondary hover:bg-secondary-700 hover:text-white" data-testid="add-image-url-button">
                    Bæta við
                  </Button>
                </div>
              </div>

              {/* Image Gallery with Drag-and-Drop */}
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={property.images} strategy={rectSortingStrategy}>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="image-gallery-dnd">
                    {property.images.map((image, index) => (
                      <SortableImage key={image} id={image} image={image} index={index} removeImage={removeImage} />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
              {property.images.length > 0 && (
                <p className="text-xs text-muted-foreground mt-2">
                  Fjöldi mynda: {property.images.length} | Fyrsta myndin er hero mynd á forsíðu
                </p>
              )}
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-primary hover:bg-primary-600 hover:text-white text-white px-8 py-4 rounded-full font-ui font-medium tracking-wide shadow-lg shadow-primary-200/50 hover:shadow-xl transition-all duration-200"
              data-testid="save-property-button"
            >
              <Save className="w-5 h-5 mr-2" />
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default PropertyManagement;